import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { CheckCircle, Upload, ChevronLeft, ChevronRight, Send } from 'lucide-react';

const api = axios.create({ baseURL: '' });

interface CandidateData {
  firstName: string; lastName: string; email: string; phone: string; alternatePhone: string;
  dateOfBirth: string; gender: string; maritalStatus: string; bloodGroup: string; disability: string;
  aadharNumber: string; panNumber: string; uanNumber: string; esicNumber: string; drivingLicense: string;
  currentAddress: { street: string; city: string; state: string; pincode: string };
  permanentAddress: { street: string; city: string; state: string; pincode: string };
  bankName: string; accountNumber: string; ifscCode: string; branchName: string;
  family: { name: string; relation: string; contact: string }[];
  education: { schoolName: string; degree: string; fieldOfStudy: string; completionDate: string; additionalNotes: string }[];
  experience: { company: string; occupation: string; summary: string; duration: string; reasonForLeaving: string; currentlyWorkHere: boolean }[];
  isFresher: boolean; highestQualification: string; currentSalary: number; skillSet: string[];
  candidateDocuments: { code: string; label: string; required: boolean; applicableForFreshers: boolean; status: string; fileUrl: string }[];
  onboardingStatus: string; completionPercent: number; designation: string; department: string;
  candidateId: string; dateOfJoining: string;
}

const EMPTY: CandidateData = {
  firstName: '', lastName: '', email: '', phone: '', alternatePhone: '',
  dateOfBirth: '', gender: '', maritalStatus: '', bloodGroup: '', disability: 'None',
  aadharNumber: '', panNumber: '', uanNumber: '', esicNumber: '', drivingLicense: '',
  currentAddress: { street: '', city: '', state: '', pincode: '' },
  permanentAddress: { street: '', city: '', state: '', pincode: '' },
  bankName: '', accountNumber: '', ifscCode: '', branchName: '',
  family: [{ name: '', relation: 'father', contact: '' }, { name: '', relation: 'mother', contact: '' }],
  education: [{ schoolName: '', degree: '10th', fieldOfStudy: '', completionDate: '', additionalNotes: '' }, { schoolName: '', degree: '12th', fieldOfStudy: '', completionDate: '', additionalNotes: '' }, { schoolName: '', degree: 'Bachelors', fieldOfStudy: '', completionDate: '', additionalNotes: '' }],
  experience: [],
  isFresher: false, highestQualification: '', currentSalary: 0, skillSet: [],
  candidateDocuments: [], onboardingStatus: 'pending_info', completionPercent: 0,
  designation: '', department: '', candidateId: '', dateOfJoining: '',
};

const STEPS = ['Personal Details', 'Address & Bank', 'Family Details', 'Education', 'Work Experience', 'Document Upload', 'Review & Submit'];

export function EJoiningPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<CandidateData>(EMPTY);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    api.get(`/api/onboarding-portal/portal/${token}`).then(r => {
      const c = r.data.candidate;
      setData({ ...EMPTY, ...c, dateOfBirth: c.dateOfBirth?.slice(0, 10) || '', dateOfJoining: c.dateOfJoining?.slice(0, 10) || '' });
      if (c.onboardingStatus !== 'pending_info') setSubmitted(true);
      setLoading(false);
    }).catch(() => { toast.error('Invalid or expired link.'); setLoading(false); });
  }, [token]);

  const save = async (partial?: Partial<CandidateData>) => {
    if (!token) return;
    setSaving(true);
    try {
      const payload = partial || data;
      await api.put(`/api/onboarding-portal/portal/${token}`, payload);
    } catch { /* silent auto-save */ }
    setSaving(false);
  };

  const next = async () => { await save(); setStep(s => Math.min(s + 1, STEPS.length - 1)); };
  const prev = () => setStep(s => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    if (!token) return;
    try {
      await save();
      await api.post(`/api/onboarding-portal/portal/${token}/submit`);
      setSubmitted(true);
      toast.success('Information submitted successfully! HR will review your documents.');
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Submission failed.'); }
  };

  const uploadDoc = async (code: string, file: File) => {
    if (!token) return;
    setUploadingDoc(code);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data: result } = await api.post(
        `/api/onboarding-portal/portal/${token}/documents/${code}/upload`,
        form,
        { headers: { 'Content-Type': null } }, // let the browser set the multipart boundary
      );
      const fileUrl = result?.document?.fileUrl || '';
      setData(d => ({ ...d, candidateDocuments: d.candidateDocuments.map(doc => doc.code === code ? { ...doc, status: 'submitted', fileUrl } : doc) }));
      toast.success('Document uploaded.');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Upload failed.');
    } finally {
      setUploadingDoc(null);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><p className="text-lg">Loading E-Joining Portal...</p></div>;
  if (!token) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><p className="text-danger-text">Invalid link.</p></div>;

  if (submitted) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
        <CheckCircle className="h-16 w-16 text-success-text mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Information Submitted!</h2>
        <p className="text-gray-600 mb-4">Thank you, {data.firstName}. Your details have been received. HR will review and verify your documents.</p>
        <p className="text-sm text-gray-500">You can still upload missing documents using this link.</p>
        <div className="mt-6"><button onClick={() => { setSubmitted(false); setStep(5); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">Upload More Documents</button></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-indigo-900 text-white py-6 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl font-bold">ZYNX — E-Joining Portal</h1>
          <p className="text-indigo-200 text-sm mt-1">Welcome{data.firstName ? `, ${data.firstName}` : ''}! Please complete all sections below.</p>
          {data.candidateId && <p className="text-indigo-300 text-xs mt-1">ID: {data.candidateId} | {data.designation} — {data.department}</p>}
        </div>
      </div>

      {/* Progress bar */}
      <div className="max-w-3xl mx-auto px-4 mt-4">
        <div className="flex items-center gap-1 mb-2">
          {STEPS.map((s, i) => (
            <button key={i} onClick={() => setStep(i)} className={`flex-1 h-2 rounded-full transition ${i <= step ? 'bg-indigo-600' : 'bg-gray-200'}`} title={s} />
          ))}
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>Step {step + 1} of {STEPS.length}</span>
          <span>{STEPS[step]}</span>
          <span>{saving ? 'Saving...' : `${data.completionPercent || 0}% complete`}</span>
        </div>
      </div>

      {/* Form content */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-sm border p-6 min-h-[400px]">
          {step === 0 && <StepPersonal data={data} setData={setData} />}
          {step === 1 && <StepAddressBank data={data} setData={setData} />}
          {step === 2 && <StepFamily data={data} setData={setData} />}
          {step === 3 && <StepEducation data={data} setData={setData} />}
          {step === 4 && <StepExperience data={data} setData={setData} />}
          {step === 5 && <StepDocuments data={data} uploadDoc={uploadDoc} uploadingDoc={uploadingDoc} />}
          {step === 6 && <StepReview data={data} onSubmit={handleSubmit} />}
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between mt-4">
          <button onClick={prev} disabled={step === 0} className="flex items-center gap-1 px-4 py-2 rounded-lg border text-sm disabled:opacity-30 hover:bg-gray-50"><ChevronLeft className="h-4 w-4" />Previous</button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} className="flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700">Next<ChevronRight className="h-4 w-4" /></button>
          ) : (
            <button onClick={handleSubmit} className="flex items-center gap-1 px-5 py-2 rounded-lg bg-success text-white text-sm hover:bg-success font-medium"><Send className="h-4 w-4" />Submit All Information</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══ Step Components ═════════════════════════════════════════════════════════

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return <div><label className="block text-sm font-medium text-gray-700 mb-1">{label}{required && <span className="text-danger-text"> *</span>}</label>{children}</div>;
}

function Input({ value, onChange, ...props }: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> & { value: string; onChange: (v: string) => void }) {
  return <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-900 focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none" value={value} onChange={e => onChange(e.target.value)} {...props} />;
}

function Sel({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-900 focus:ring-2 focus:ring-indigo-300 outline-none" value={value} onChange={e => onChange(e.target.value)}>{options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select>;
}

function StepPersonal({ data, setData }: { data: CandidateData; setData: (fn: (d: CandidateData) => CandidateData) => void }) {
  const set = (k: keyof CandidateData, v: any) => setData(d => ({ ...d, [k]: v }));
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold border-b pb-2">Personal Details</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="First Name" required><Input value={data.firstName} onChange={v => set('firstName', v)} placeholder="Enter first name" /></Field>
        <Field label="Last Name" required><Input value={data.lastName} onChange={v => set('lastName', v)} placeholder="Enter last name" /></Field>
        <Field label="Email" required><Input value={data.email} onChange={v => set('email', v)} type="email" placeholder="your@email.com" /></Field>
        <Field label="Phone" required><Input value={data.phone} onChange={v => set('phone', v)} placeholder="9876543210" /></Field>
        <Field label="Alternate Phone"><Input value={data.alternatePhone} onChange={v => set('alternatePhone', v)} /></Field>
        <Field label="Date of Birth" required><Input value={data.dateOfBirth} onChange={v => set('dateOfBirth', v)} type="date" /></Field>
        <Field label="Gender"><Sel value={data.gender} onChange={v => set('gender', v)} options={[{value:'',label:'Select...'},{value:'male',label:'Male'},{value:'female',label:'Female'},{value:'other',label:'Other'}]} /></Field>
        <Field label="Marital Status"><Sel value={data.maritalStatus} onChange={v => set('maritalStatus', v)} options={[{value:'',label:'Select...'},{value:'single',label:'Single'},{value:'married',label:'Married'},{value:'divorced',label:'Divorced'},{value:'widowed',label:'Widowed'}]} /></Field>
        <Field label="Blood Group"><Input value={data.bloodGroup} onChange={v => set('bloodGroup', v)} placeholder="e.g. B+" /></Field>
        <Field label="Any Disability"><Input value={data.disability} onChange={v => set('disability', v)} placeholder="None" /></Field>
      </div>
      <h3 className="text-sm font-semibold mt-4 pt-2 border-t">Identity Numbers</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Aadhar Number" required><Input value={data.aadharNumber} onChange={v => set('aadharNumber', v)} placeholder="XXXX XXXX XXXX" /></Field>
        <Field label="PAN Number" required><Input value={data.panNumber} onChange={v => set('panNumber', v)} placeholder="ABCDE1234F" /></Field>
        <Field label="UAN Number"><Input value={data.uanNumber} onChange={v => set('uanNumber', v)} placeholder="If available" /></Field>
        <Field label="ESIC Number"><Input value={data.esicNumber} onChange={v => set('esicNumber', v)} placeholder="If available" /></Field>
        <Field label="Driving License"><Input value={data.drivingLicense} onChange={v => set('drivingLicense', v)} placeholder="If applicable" /></Field>
      </div>
    </div>
  );
}

function StepAddressBank({ data, setData }: { data: CandidateData; setData: (fn: (d: CandidateData) => CandidateData) => void }) {
  const setAddr = (which: 'currentAddress' | 'permanentAddress', k: string, v: string) => setData(d => ({ ...d, [which]: { ...d[which], [k]: v } }));
  const set = (k: keyof CandidateData, v: any) => setData(d => ({ ...d, [k]: v }));
  const [sameAddr, setSameAddr] = useState(false);

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold border-b pb-2">Address Details</h2>
      <h3 className="text-sm font-medium">Current Address</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2"><Field label="Street"><Input value={data.currentAddress.street} onChange={v => setAddr('currentAddress', 'street', v)} /></Field></div>
        <Field label="City"><Input value={data.currentAddress.city} onChange={v => setAddr('currentAddress', 'city', v)} /></Field>
        <Field label="State"><Input value={data.currentAddress.state} onChange={v => setAddr('currentAddress', 'state', v)} /></Field>
        <Field label="Pin Code"><Input value={data.currentAddress.pincode} onChange={v => setAddr('currentAddress', 'pincode', v)} /></Field>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <input type="checkbox" checked={sameAddr} onChange={e => { setSameAddr(e.target.checked); if (e.target.checked) setData(d => ({ ...d, permanentAddress: { ...d.currentAddress } })); }} className="rounded" />
        <label className="text-sm">Permanent address same as current</label>
      </div>
      {!sameAddr && <>
        <h3 className="text-sm font-medium mt-3">Permanent Address</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><Field label="Street"><Input value={data.permanentAddress.street} onChange={v => setAddr('permanentAddress', 'street', v)} /></Field></div>
          <Field label="City"><Input value={data.permanentAddress.city} onChange={v => setAddr('permanentAddress', 'city', v)} /></Field>
          <Field label="State"><Input value={data.permanentAddress.state} onChange={v => setAddr('permanentAddress', 'state', v)} /></Field>
          <Field label="Pin Code"><Input value={data.permanentAddress.pincode} onChange={v => setAddr('permanentAddress', 'pincode', v)} /></Field>
        </div>
      </>}
      <h2 className="text-lg font-semibold border-b pb-2 mt-6">Bank Details</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Bank Name" required><Input value={data.bankName} onChange={v => set('bankName', v)} placeholder="e.g. HDFC Bank" /></Field>
        <Field label="Account Number" required><Input value={data.accountNumber} onChange={v => set('accountNumber', v)} /></Field>
        <Field label="IFSC Code" required><Input value={data.ifscCode} onChange={v => set('ifscCode', v)} placeholder="e.g. HDFC0001234" /></Field>
        <Field label="Branch Name"><Input value={data.branchName} onChange={v => set('branchName', v)} /></Field>
      </div>
    </div>
  );
}

function StepFamily({ data, setData }: { data: CandidateData; setData: (fn: (d: CandidateData) => CandidateData) => void }) {
  const addRow = () => setData(d => ({ ...d, family: [...d.family, { name: '', relation: 'other', contact: '' }] }));
  const removeRow = (i: number) => setData(d => ({ ...d, family: d.family.filter((_, idx) => idx !== i) }));
  const updateRow = (i: number, k: string, v: string) => setData(d => ({ ...d, family: d.family.map((f, idx) => idx === i ? { ...f, [k]: v } : f) }));

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold border-b pb-2">Family Details</h2>
      <p className="text-sm text-gray-500">Add your family members (emergency contacts).</p>
      <div className="space-y-3">
        {data.family.map((f, i) => (
          <div key={i} className="grid grid-cols-[1fr_140px_1fr_auto] gap-2 items-end">
            <Field label={i === 0 ? 'Name' : ''}><Input value={f.name} onChange={v => updateRow(i, 'name', v)} placeholder="Full name" /></Field>
            <Field label={i === 0 ? 'Relation' : ''}><Sel value={f.relation} onChange={v => updateRow(i, 'relation', v)} options={[{value:'father',label:'Father'},{value:'mother',label:'Mother'},{value:'spouse',label:'Spouse'},{value:'brother',label:'Brother'},{value:'sister',label:'Sister'},{value:'child',label:'Child'},{value:'other',label:'Other'}]} /></Field>
            <Field label={i === 0 ? 'Contact' : ''}><Input value={f.contact} onChange={v => updateRow(i, 'contact', v)} placeholder="Phone" /></Field>
            <button onClick={() => removeRow(i)} className="p-2 text-danger-text hover:bg-danger/10 rounded" title="Remove">✕</button>
          </div>
        ))}
      </div>
      {data.family.length < 7 && <button onClick={addRow} className="text-sm text-indigo-600 hover:underline">+ Add family member</button>}
    </div>
  );
}

function StepEducation({ data, setData }: { data: CandidateData; setData: (fn: (d: CandidateData) => CandidateData) => void }) {
  const addRow = () => setData(d => ({ ...d, education: [...d.education, { schoolName: '', degree: '', fieldOfStudy: '', completionDate: '', additionalNotes: '' }] }));
  const removeRow = (i: number) => setData(d => ({ ...d, education: d.education.filter((_, idx) => idx !== i) }));
  const updateRow = (i: number, k: string, v: string) => setData(d => ({ ...d, education: d.education.map((e, idx) => idx === i ? { ...e, [k]: v } : e) }));

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold border-b pb-2">Education</h2>
      <div className="space-y-3">
        {data.education.map((e, i) => (
          <div key={i} className="p-3 border rounded-lg bg-gray-50 space-y-2">
            <div className="flex justify-between items-center"><span className="text-xs font-medium text-gray-500">Entry {i + 1}</span><button onClick={() => removeRow(i)} className="text-xs text-danger-text">Remove</button></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Input value={e.schoolName} onChange={v => updateRow(i, 'schoolName', v)} placeholder="School / College name" />
              <Input value={e.degree} onChange={v => updateRow(i, 'degree', v)} placeholder="Degree (10th, 12th, B.Tech, MBA...)" />
              <Input value={e.fieldOfStudy} onChange={v => updateRow(i, 'fieldOfStudy', v)} placeholder="Field of study" />
              <Input value={e.completionDate?.slice(0, 10) || ''} onChange={v => updateRow(i, 'completionDate', v)} type="date" />
            </div>
          </div>
        ))}
      </div>
      <button onClick={addRow} className="text-sm text-indigo-600 hover:underline">+ Add education entry</button>
    </div>
  );
}

function StepExperience({ data, setData }: { data: CandidateData; setData: (fn: (d: CandidateData) => CandidateData) => void }) {
  const set = (k: keyof CandidateData, v: any) => setData(d => ({ ...d, [k]: v }));
  const addRow = () => setData(d => ({ ...d, experience: [...d.experience, { company: '', occupation: '', summary: '', duration: '', reasonForLeaving: '', currentlyWorkHere: false }] }));
  const removeRow = (i: number) => setData(d => ({ ...d, experience: d.experience.filter((_, idx) => idx !== i) }));
  const updateRow = (i: number, k: string, v: any) => setData(d => ({ ...d, experience: d.experience.map((e, idx) => idx === i ? { ...e, [k]: v } : e) }));

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold border-b pb-2">Work Experience</h2>
      <div className="flex items-center gap-2 p-3 border rounded-lg bg-info/10">
        <input type="checkbox" checked={data.isFresher} onChange={e => set('isFresher', e.target.checked)} className="rounded" />
        <label className="text-sm font-medium">I am a fresher (no previous work experience)</label>
      </div>
      {!data.isFresher && <>
        <div className="space-y-3">
          {data.experience.map((e, i) => (
            <div key={i} className="p-3 border rounded-lg bg-gray-50 space-y-2">
              <div className="flex justify-between items-center"><span className="text-xs font-medium text-gray-500">Company {i + 1}</span><button onClick={() => removeRow(i)} className="text-xs text-danger-text">Remove</button></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Input value={e.company} onChange={v => updateRow(i, 'company', v)} placeholder="Company name" />
                <Input value={e.occupation} onChange={v => updateRow(i, 'occupation', v)} placeholder="Job title / role" />
                <Input value={e.duration} onChange={v => updateRow(i, 'duration', v)} placeholder="Duration (e.g. 2 years)" />
                <Input value={e.reasonForLeaving} onChange={v => updateRow(i, 'reasonForLeaving', v)} placeholder="Reason for leaving" />
              </div>
            </div>
          ))}
        </div>
        <button onClick={addRow} className="text-sm text-indigo-600 hover:underline">+ Add work experience</button>
      </>}
      <h3 className="text-sm font-semibold mt-4 pt-2 border-t">Additional</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Highest Qualification"><Input value={data.highestQualification} onChange={v => set('highestQualification', v)} placeholder="e.g. MBA, B.Tech" /></Field>
        <Field label="Current/Last Salary (Annual ₹)"><Input value={String(data.currentSalary || '')} onChange={v => set('currentSalary', Number(v) || 0)} type="number" /></Field>
      </div>
    </div>
  );
}

function StepDocuments({
  data,
  uploadDoc,
  uploadingDoc,
}: {
  data: CandidateData;
  uploadDoc: (code: string, file: File) => void;
  uploadingDoc: string | null;
}) {
  const docs = data.candidateDocuments || [];
  const statusIcon = (s: string) => {
    if (s === 'submitted' || s === 'verified') return <CheckCircle className="h-4 w-4 text-success-text" />;
    if (s === 'not_applicable') return <span className="text-xs text-gray-400">N/A</span>;
    if (s === 'rejected') return <span className="text-xs text-danger-text font-medium">Rejected</span>;
    return <span className="text-xs text-warning-text">Pending</span>;
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold border-b pb-2">Document Upload</h2>
      <p className="text-sm text-gray-500">Upload all required documents (PDF, JPG or PNG). Items marked * are mandatory.</p>
      <div className="space-y-2">
        {docs.map(doc => (
          <div key={doc.code} className={`flex items-center justify-between p-3 border rounded-lg ${doc.status === 'not_applicable' ? 'bg-gray-50 opacity-60' : 'bg-white'}`}>
            <div className="flex items-center gap-3">
              {statusIcon(doc.status)}
              <span className="text-sm">{doc.label}{doc.required && doc.status !== 'not_applicable' ? <span className="text-danger-text"> *</span> : ''}</span>
            </div>
            {doc.status === 'not_applicable' ? (
              <span className="text-xs text-gray-400 italic">Not Applicable</span>
            ) : doc.status === 'pending' || doc.status === 'rejected' ? (
              <label className="flex items-center gap-1 px-3 py-1.5 text-xs bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 font-medium cursor-pointer">
                <Upload className="h-3.5 w-3.5" />
                {uploadingDoc === doc.code ? 'Uploading...' : 'Upload'}
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  className="hidden"
                  disabled={uploadingDoc === doc.code}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = ''; // allow re-selecting the same file after a failed upload
                    if (file) uploadDoc(doc.code, file);
                  }}
                />
              </label>
            ) : (
              <span className="text-xs text-success-text font-medium">Uploaded ✓</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StepReview({ data, onSubmit }: { data: CandidateData; onSubmit: () => void }) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold border-b pb-2">Review & Submit</h2>
      <p className="text-sm text-gray-500">Please review your information before submitting.</p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <div><span className="text-gray-500">Name:</span> <strong>{data.firstName} {data.lastName}</strong></div>
        <div><span className="text-gray-500">Email:</span> {data.email}</div>
        <div><span className="text-gray-500">Phone:</span> {data.phone}</div>
        <div><span className="text-gray-500">DOB:</span> {data.dateOfBirth}</div>
        <div><span className="text-gray-500">Gender:</span> {data.gender}</div>
        <div><span className="text-gray-500">Blood Group:</span> {data.bloodGroup}</div>
        <div><span className="text-gray-500">City:</span> {data.currentAddress.city}</div>
        <div><span className="text-gray-500">Bank:</span> {data.bankName}</div>
        <div><span className="text-gray-500">Qualification:</span> {data.highestQualification}</div>
        <div><span className="text-gray-500">Fresher:</span> {data.isFresher ? 'Yes' : 'No'}</div>
      </div>
      <div className="mt-4 p-3 border rounded-lg bg-warning/10 text-sm text-warning-text">
        <strong>Note:</strong> After submission, HR will verify your documents. You can still upload missing documents using this link.
      </div>
      <div className="text-center mt-6">
        <button onClick={onSubmit} className="px-6 py-3 bg-success text-white rounded-lg font-medium hover:bg-success text-base">Submit All Information</button>
      </div>
    </div>
  );
}
